import React, { useState, useEffect } from "react";
import { toast, Toaster } from "react-hot-toast";
import axios from "axios";
import Loading from "../components/Loading";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search } from "lucide-react";

function CourseManagement() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    
    const [newCoursesList, setNewCoursesList] = useState([{ course_name: "", batch_id: "", plan_id: "" }]);
    const [courseToEdit, setCourseToEdit] = useState(null);
    const [editedCourseName, setEditedCourseName] = useState("");
    const [editedBatchId, setEditedBatchId] = useState("");
    const [editedPlanId, setEditedPlanId] = useState("");
    const [courseToDelete, setCourseToDelete] = useState(null);

    // Resolve API URL dynamically based on environment
    const getApiUrl = (path) => {
        const isLocal = window.location.hostname === "localhost";
        const base = isLocal ? "http://localhost:3000" : "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod";
        return `${base}${path}`;
    };

    const fetchCourses = async () => {
        setLoading(true);
        try {
            const response = await axios.get(getApiUrl("/api/courses"));
            setCourses(response.data || []);
        } catch (error) {
            toast.error(error.response?.data?.error || "Error fetching courses");
            console.error("Error fetching courses:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCourse = async (e) => {
        e.preventDefault();
        const validCourses = newCoursesList.filter(c => c.course_name.trim());
        if (validCourses.length === 0) {
            toast.error("At least one course name is required");
            return;
        }

        try {
            const response = await axios.post(getApiUrl("/api/courses"), validCourses);
            toast.success(response.data.message || "Courses created successfully");
            setShowCreateModal(false);
            setNewCoursesList([{ course_name: "", batch_id: "", plan_id: "" }]);
            fetchCourses();
        } catch (error) {
            toast.error(error.response?.data?.error || "Error creating courses");
            console.error("Error creating courses:", error);
        }
    };

    const handleEditClick = (course) => {
        setCourseToEdit(course);
        setEditedCourseName(course.course_name);
        setEditedBatchId(course.batch_id || "");
        setEditedPlanId(course.plan_id || "");
        setShowEditModal(true);
    };

    const handleEditCourse = async (e) => {
        e.preventDefault();
        if (!editedCourseName.trim()) {
            toast.error("Course name is required");
            return;
        }

        try {
            const response = await axios.put(getApiUrl("/api/courses"), {
                old_course_name: courseToEdit.course_name,
                new_course_name: editedCourseName.trim(),
                batch_id: editedBatchId.trim(),
                plan_id: editedPlanId.trim()
            });
            toast.success(response.data.message || "Course updated successfully");
            setShowEditModal(false);
            setCourseToEdit(null);
            setEditedCourseName("");
            setEditedBatchId("");
            setEditedPlanId("");
            fetchCourses();
        } catch (error) {
            toast.error(error.response?.data?.error || "Error updating course");
            console.error("Error updating course:", error);
        }
    };

    const handleDeleteClick = (course) => {
        setCourseToDelete(course);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!courseToDelete) return;

        try {
            const response = await axios.delete(getApiUrl("/api/courses"), {
                data: { course_name: courseToDelete.course_name }
            });
            toast.success(response.data.message || "Course deleted successfully");
            fetchCourses();
        } catch (error) {
            toast.error(error.response?.data?.error || "Error deleting course");
            console.error("Error deleting course:", error);
        } finally {
            setShowDeleteDialog(false);
            setCourseToDelete(null);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    // Filter courses based on search query
    const filteredCourses = courses.filter(course =>
        (course.course_name || "").toLowerCase().includes(searchQuery.toLowerCase())
    );

    const columns = [
        {
            accessorKey: "course_name",
            header: "Course Name",
        },
        {
            accessorKey: "batch_id",
            header: "Batch ID",
            cell: ({ row }) => row.original.batch_id || "N/A"
        },
        {
            accessorKey: "plan_id",
            header: "Plan ID",
            cell: ({ row }) => row.original.plan_id || "N/A"
        },
        {
            accessorKey: "createdAt",
            header: "Created At",
            cell: ({ row }) => {
                const date = row.original.createdAt;
                return date ? new Date(date).toLocaleString("en-IN") : "N/A";
            }
        },
        {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(row.original)}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteClick(row.original)}
                    >
                        Delete
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div className="p-6">
            <Toaster
                position="bottom-left"
                toastOptions={{
                    style: {
                        background: "#ff8e00",
                        color: "#2e2c2d",
                    },
                }}
            />

            <Card>
                <CardHeader>
                    <CardTitle>Course Management</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-between items-center mb-6">
                        <div className="relative w-72">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-8"
                            />
                        </div>
                        <div>
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                variant="default"
                            >
                                Add Course
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <Loading />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={filteredCourses}
                            searchable={false}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent style={{ maxWidth: "650px", width: "95%" }}>
                    <DialogHeader>
                        <DialogTitle>Add New Courses</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCourse}>
                        <div style={{ maxHeight: "350px", overflowY: "auto", paddingRight: "8px" }} className="flex flex-col gap-4 py-2 scrollbar-thin">
                            {newCoursesList.map((course, index) => (
                                <div key={index} className="border p-3 rounded-lg relative flex flex-col gap-2 bg-gray-50/50">
                                    {newCoursesList.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const list = [...newCoursesList];
                                                list.splice(index, 1);
                                                setNewCoursesList(list);
                                            }}
                                            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                        >
                                            <i className="fa-solid fa-trash"></i>
                                        </button>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pr-6">
                                        <div className="flex flex-col gap-1">
                                            <Label htmlFor={`courseName-${index}`} className="text-xs">Course Name</Label>
                                            <Input
                                                id={`courseName-${index}`}
                                                value={course.course_name}
                                                onChange={(e) => {
                                                    const list = [...newCoursesList];
                                                    list[index].course_name = e.target.value;
                                                    setNewCoursesList(list);
                                                }}
                                                placeholder="Course Name"
                                                required
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <Label htmlFor={`batchId-${index}`} className="text-xs">Batch ID</Label>
                                            <Input
                                                id={`batchId-${index}`}
                                                value={course.batch_id}
                                                onChange={(e) => {
                                                    const list = [...newCoursesList];
                                                    list[index].batch_id = e.target.value;
                                                    setNewCoursesList(list);
                                                }}
                                                placeholder="Batch ID"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <Label htmlFor={`planId-${index}`} className="text-xs">Plan ID</Label>
                                            <Input
                                                id={`planId-${index}`}
                                                value={course.plan_id}
                                                onChange={(e) => {
                                                    const list = [...newCoursesList];
                                                    list[index].plan_id = e.target.value;
                                                    setNewCoursesList(list);
                                                }}
                                                placeholder="Plan ID"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-start my-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setNewCoursesList([...newCoursesList, { course_name: "", batch_id: "", plan_id: "" }])}
                            >
                                <i className="fa-solid fa-plus mr-1"></i> Add Another Course
                            </Button>
                        </div>
                        <DialogFooter>
                            <Button type="submit">Submit Courses</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Course Details</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEditCourse}>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label htmlFor="editCourseName">Course Name</Label>
                                <Input
                                    id="editCourseName"
                                    value={editedCourseName}
                                    onChange={(e) => setEditedCourseName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="editBatchId">Batch ID</Label>
                                <Input
                                    id="editBatchId"
                                    value={editedBatchId}
                                    onChange={(e) => setEditedBatchId(e.target.value)}
                                    placeholder="Enter Batch ID"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="editPlanId">Plan ID</Label>
                                <Input
                                    id="editPlanId"
                                    value={editedPlanId}
                                    onChange={(e) => setEditedPlanId(e.target.value)}
                                    placeholder="Enter Plan ID"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="submit">Save Changes</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Alert Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the course
                            {courseToDelete && ` "${courseToDelete.course_name}"`}.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteConfirm}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default CourseManagement;
